import { useEffect, useMemo, useState } from "react";
import { Fragment } from "react/jsx-runtime";
import { DateTime } from "luxon";
import { useShallow } from "zustand/shallow";
import { timezones } from "@/lib/timezone";
import { cn } from "@/lib/utils";
import dateStringToLocalDate from "@/utils/dateStringToLocalDate";
import { useCompanyScheduleInterviewSheet } from "./store";

import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Calendar } from "@/components/ui/calendar";
import { TimeInput } from "@/components/ui/input";
import CreateUpdateInterviewDialog from "@/components/forms/company/interview";
import { Check, Loader2, User } from "lucide-react";

import { useRequestInterviewAvailability } from "./request-availability";

import { useCompanyScheduleInterview } from "./mutations";
import { useCompanyCandidateAvailabilityQuery } from "@/queries/company/availability";
import { useCompanyTeamQuery } from "@/queries/company/team";

import { weekdayList } from "@/types";

const formatTime = (time: string, zone: string) =>
  DateTime.fromISO(`1970-01-01T${time}`, { zone }).toFormat("h:mm a");

const CompanyScheduleInterviewSheet = () => {
  const candidate = useCompanyScheduleInterviewSheet((s) => s.candidate);
  const [opportunityID, interviewID] = useCompanyScheduleInterviewSheet(
    useShallow((s) => [s.opportunity?.id, s.interview?.id]),
  );
  const interview = useCompanyScheduleInterviewSheet((s) => s.interview);
  const time = useCompanyScheduleInterviewSheet((s) => s.time);
  const open = useCompanyScheduleInterviewSheet((s) => s.open);
  const setOpen = useCompanyScheduleInterviewSheet((s) => s.setOpen);
  const isUpdating = useCompanyScheduleInterviewSheet((s) => s.isUpdating);
  const isInvalid = useCompanyScheduleInterviewSheet((s) => s.invalid);
  const successOpen = useCompanyScheduleInterviewSheet((s) => s.successOpen);
  const setSuccessOpen = useCompanyScheduleInterviewSheet(
    (s) => s.setSuccessOpen,
  );

  const { candidateAvailability } = useCompanyCandidateAvailabilityQuery(
    opportunityID,
    interviewID,
    candidate?.candidateId,
  );

  const {
    scheduleInterview,
    scheduleInterviewPending,
    scheduleInterviewError,
  } = useCompanyScheduleInterview();

  const hasCandidateAvailability = useMemo(
    () =>
      candidateAvailability?.general.some(
        (day) => day.some(({ placeholder }) => !placeholder),
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
      ) || Boolean(candidateAvailability?.specific.length),
    [candidateAvailability],
  );

  return (
    <>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent className="w-full max-w-full sm:max-w-md md:max-w-lg bg-background">
          <SheetHeader className="pb-6">
            <SheetTitle>Schedule an Interview</SheetTitle>
            <SheetDescription className="sr-only">
              Enter your availability for an interview for the selected
              opportunity
            </SheetDescription>
            {scheduleInterviewError && (
              <div className="border border-destructive text-destructive p-2 rounded-md text-sm text-center w-full bg-destructive/10">
                <p>
                  An error occurred while trying to schedule the interview,
                  please try again later
                </p>
              </div>
            )}
          </SheetHeader>
          <ScrollArea className="h-[calc(100vh-4.75rem)]" type="scroll">
            {hasCandidateAvailability ? (
              <>
                <CandidateAvailability />
                <InterviewCalendar />
                <TimeEntry />
              </>
            ) : (
              <div>
                <p className="py-2 text-sm text-muted-foreground text-center w-full">
                  {candidate?.name
                    ? `${candidate?.name} has not added their availability yet`
                    : "Candidate's availability has not been added yet"}
                </p>
              </div>
            )}
            <Interviewers />
            {hasCandidateAvailability && (
              <Button
                variant={"secondary"}
                className="mt-8 w-full"
                disabled={isInvalid || !time || !interview?.participants.length}
                loading={scheduleInterviewPending}
                onClick={() => scheduleInterview()}
              >
                {isUpdating ? "Reschedule" : "Schedule"} Interview
              </Button>
            )}
            <RequestAvailability closeParent={() => setOpen(false)} />
            <Button
              variant="outline"
              className="mt-2 w-full"
              onClick={() => setOpen(false)}
            >
              Cancel
            </Button>
          </ScrollArea>
        </SheetContent>
      </Sheet>
      <Dialog open={successOpen} onOpenChange={setSuccessOpen}>
        <DialogContent>
          <DialogHeader>
            <div className="space-y-4">
              <div className="flex items-center justify-center size-12 box-content border-[6px] border-accent-og rounded-full">
                <Check className="size-7 text-secondary" />
              </div>
              <DialogTitle>
                Your interview has been marked as scheduled!
              </DialogTitle>
              <DialogDescription>
                Once your interview is complete, return to the dashboard to
                proceed the candidate to the next step.
              </DialogDescription>
            </div>

            <DialogFooter className="w-full justify-center">
              <DialogClose asChild>
                <Button
                  variant="secondary"
                  className="mt-4 w-full"
                  onClick={() => setOpen(false)}
                >
                  Return To Dashboard
                </Button>
              </DialogClose>
            </DialogFooter>
          </DialogHeader>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CompanyScheduleInterviewSheet;

const CandidateAvailability = () => {
  const candidate = useCompanyScheduleInterviewSheet((s) => s.candidate);
  const [opportunityID, interviewID] = useCompanyScheduleInterviewSheet(
    useShallow((s) => [s.opportunity?.id, s.interview?.id]),
  );
  const { candidateAvailability, candidateAvailabilityLoading } =
    useCompanyCandidateAvailabilityQuery(
      opportunityID,
      interviewID,
      candidate?.candidateId,
    );

  const sortedEntries = useMemo(() => {
    const newEntries =
      candidateAvailability?.specific.reduce(
        (acc, curr) => {
          curr.dates.forEach((date) => {
            const timezone = curr.times[0]?.timezone ?? "America/Chicago";

            const i = acc.findIndex((e) => e.date === date);
            if (i < 0) {
              acc.push({
                date,
                times: [...curr.times],
                timezone,
              });
            } else {
              acc[i]?.times.push(...curr.times);
            }
          });

          return acc;
        },
        [] as {
          date: string;
          times: { startTime: string; endTime: string }[];
          timezone: string;
        }[],
      ) ?? [];

    newEntries.sort((a, b) => a.date.localeCompare(b.date));
    newEntries.forEach((entry) =>
      entry.times.sort((a, b) => a.startTime.localeCompare(b.startTime)),
    );

    return newEntries;
  }, [candidateAvailability?.specific]);

  const timezone =
    candidateAvailability?.general?.find((a) => a.length > 0)?.[0]?.timezone ??
    candidateAvailability?.specific?.find((a) => a.times.length > 0)?.times[0]
      ?.timezone;

  const timezoneName =
    timezones.flatMap((tz) => tz.timezones).find((tz) => tz.iana === timezone)
      ?.name ?? timezone;

  if (!timezoneName) {
    return null;
  }

  const hasRealGeneralAvailability = !!candidateAvailability?.general?.filter(
    (e) => e.some((t) => !t.placeholder),
  )?.length;

  return (
    <div className="border border-border shadow-md rounded-lg p-4 space-y-4">
      {candidateAvailabilityLoading ? (
        <Skeleton className="w-4/5 h-4" />
      ) : (
        <h5 className="font-semibold text-sm">
          {candidate?.name}
          {candidate?.name?.at(-1) === "s" ? "' " : "'s "}
          Availability in {timezoneName}
        </h5>
      )}
      {candidateAvailabilityLoading ? (
        <div className="space-y-1">
          {Array.from({ length: 3 }).map((_, i) => (
            <div
              key={`avail-${i}`}
              className="flex justify-between items-center text-muted-foreground text-sm"
            >
              <Skeleton className="w-3/5 h-4" />
              <Skeleton className="w-1/4 h-4" />
            </div>
          ))}
        </div>
      ) : !hasRealGeneralAvailability &&
        !candidateAvailability?.specific?.length ? (
        <div>
          <p className="py-2 text-sm text-muted-foreground text-center w-full">
            {candidate?.name
              ? `${candidate?.name} has not added their availability yet`
              : "Candidate's availability has not been added yet"}
          </p>
        </div>
      ) : (
        <div>
          <div className="grid grid-cols-[auto,auto] justify-between text-muted-foreground text-sm">
            {candidateAvailability?.general
              .map((d) => d.filter((e) => !e.placeholder))
              .map((day, i) => {
                if (day.length === 0) {
                  return null;
                }

                return (
                  <Fragment key={`general-avail-${i}`}>
                    <p>{weekdayList[i]}s</p>
                    <div className="text-right">
                      {day.map((time, j) => (
                        <p key={j}>
                          {formatTime(time.startTime, time.timezone)} -{" "}
                          {formatTime(time.endTime, time.timezone)}
                        </p>
                      ))}
                    </div>
                  </Fragment>
                );
              })}
          </div>
          {!!sortedEntries.length && (
            <>
              <div className="h-px bg-border w-full my-2"></div>
              <div className="grid grid-cols-[auto,auto] justify-between text-muted-foreground text-sm">
                {sortedEntries.map(({ date, times, timezone }, i) => (
                  <Fragment key={`specific-avail-${i}`}>
                    <p>{dateStringToLocalDate(date)?.toLocaleDateString()}</p>
                    <div className="text-right">
                      {times.map((time, j) => (
                        <p key={j}>
                          {formatTime(time.startTime, timezone)} -{" "}
                          {formatTime(time.endTime, timezone)}
                        </p>
                      ))}
                    </div>
                  </Fragment>
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

const InterviewCalendar = () => {
  const selectedDate = useCompanyScheduleInterviewSheet((s) => s.selectedDate);
  const setSelectedDate = useCompanyScheduleInterviewSheet(
    (s) => s.setSelectedDate,
  );
  const candidate = useCompanyScheduleInterviewSheet((s) => s.candidate);
  const [opportunityID, interviewID] = useCompanyScheduleInterviewSheet(
    useShallow((s) => [s.opportunity?.id, s.interview?.id]),
  );
  const { candidateAvailability } = useCompanyCandidateAvailabilityQuery(
    opportunityID,
    interviewID,
    candidate?.candidateId,
  );

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  return (
    <div className="border border-border shadow-md rounded-lg p-4 space-y-4 mt-6">
      <h5 className="text-sm font-semibold">
        When would you like to schedule the interview?
      </h5>
      <Calendar
        selected={dateStringToLocalDate(selectedDate) ?? undefined}
        onSelect={(date) => {
          setSelectedDate(
            date ? new Date(date).toLocaleDateString("en-CA") : "",
          );
        }}
        className="h-full w-full flex"
        classNames={{
          root: "!p-0",
          months:
            "flex w-full flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0 flex-1",
          month: "space-y-4 w-full flex flex-col",
          table: "w-full h-full border-collapse space-y-1",
          head_row: "",
          row: "w-full mt-2",
          cell: "[&:has([aria-selected].day-outside)]:bg-background text-center",
        }}
        mode="single"
        disabled={(d) => {
          if (d < today) {
            return true;
          }

          if (
            candidateAvailability?.general[d.getDay()]?.find(
              (e) => !e.placeholder,
            )
          ) {
            return false;
          }

          return !candidateAvailability?.specific.some(({ dates }) =>
            dates.some((date) => {
              const dateObj = dateStringToLocalDate(date);
              if (!dateObj) {
                return false;
              }

              return dateObj.toISOString() === d.toISOString();
            }),
          );
        }}
      />
    </div>
  );
};

const TimeEntry = () => {
  const invalid = useCompanyScheduleInterviewSheet((s) => s.invalid);
  const toggleInvalid = useCompanyScheduleInterviewSheet((s) => s.setInvalid);
  const time = useCompanyScheduleInterviewSheet((s) => s.time);
  const setTime = useCompanyScheduleInterviewSheet((s) => s.setTime);
  const selectedDate = useCompanyScheduleInterviewSheet((s) => s.selectedDate);
  const candidate = useCompanyScheduleInterviewSheet((s) => s.candidate);
  const interview = useCompanyScheduleInterviewSheet((s) => s.interview);
  const [opportunityID, interviewID] = useCompanyScheduleInterviewSheet(
    useShallow((s) => [s.opportunity?.id, s.interview?.id]),
  );
  const { candidateAvailability } = useCompanyCandidateAvailabilityQuery(
    opportunityID,
    interviewID,
    candidate?.candidateId,
  );

  const candidateTimezone =
    candidateAvailability?.general?.find((a) => a.length > 0)?.[0]?.timezone ??
    candidateAvailability?.specific?.find((a) => a.times.length > 0)?.times[0]
      ?.timezone;

  const timezoneParameter = { zone: candidateTimezone };

  const selectedStart = DateTime.fromISO(
    `${selectedDate}T${time}`,
    timezoneParameter,
  );
  const selectedStartLocal = selectedStart.setZone("local");

  useEffect(() => {
    if (selectedDate && time) {
      if (!candidateAvailability) {
        toggleInvalid(true);
        return;
      }

      const selectedStart = DateTime.fromISO(
        `${selectedDate}T${time}`,
        timezoneParameter,
      );
      const selectedEnd = selectedStart.plus({
        minutes: interview?.duration ?? 45,
      });

      // Luxon uses 1=Mon, 2=Tue, ... while JS uses 0=Sun, 1=Mon, ...
      const weekday = selectedStart.weekday % 7;

      // Check if the selected time is within the candidate's availability
      // Check against the general availability first
      let valid = candidateAvailability?.general[weekday]?.some((range) => {
        const rangeStart = DateTime.fromISO(
          `${selectedDate}T${range.startTime}`,
          timezoneParameter,
        );
        const rangeEnd = DateTime.fromISO(
          `${selectedDate}T${range.endTime}`,
          timezoneParameter,
        );

        return selectedStart >= rangeStart && selectedEnd <= rangeEnd;
      });

      if (valid) {
        toggleInvalid(false);
        return;
      }

      valid = candidateAvailability?.specific.some(({ dates, times }) => {
        return dates.some((date) => {
          if (selectedDate !== date) {
            return false;
          }

          return times.some((range) => {
            const rangeStart = DateTime.fromISO(
              `${selectedDate}T${range.startTime}`,
              timezoneParameter,
            );
            const rangeEnd = DateTime.fromISO(
              `${selectedDate}T${range.endTime}`,
              timezoneParameter,
            );

            return selectedStart >= rangeStart && selectedEnd <= rangeEnd;
          });
        });
      });

      if (valid) {
        toggleInvalid(false);
        return;
      }

      toggleInvalid(true);
    } else {
      toggleInvalid(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [candidateAvailability, selectedDate, time, toggleInvalid, interview]);

  return (
    <div className="border border-border shadow-md rounded-lg p-4 space-y-4 mt-6">
      {!selectedDate ? (
        <h5 className="text-sm font-semibold">
          Select a date from the calendar above to enter the agreed upon time
          for the interview
        </h5>
      ) : (
        <>
          <h5 className="text-sm font-semibold">
            {new Intl.DateTimeFormat("en-US", {
              month: "long",
              day: "numeric",
              year: "numeric",
            }).format(dateStringToLocalDate(selectedDate)!)}{" "}
            at
          </h5>
          <div className="space-y-2">
            <TimeInput
              value={time}
              className={cn({
                "border-destructive": invalid,
              })}
              onChange={(e) => {
                const input = e.target.value.toString();
                setTime(input);
              }}
            />
            {invalid && (
              <p className="text-destructive text-sm leading-none">
                Start time for a {interview?.duration ?? 45}-minute interview
                must be within the candidate&apos;s given availability
              </p>
            )}
          </div>
          {!invalid &&
            selectedStart.zoneName !== selectedStartLocal.zoneName && (
              <p className="text-sm font-semibold">
                {selectedStartLocal.toFormat(
                  "MMMM d, yyyy 'at' h:mm a 'local time' (ZZZZ)",
                )}
              </p>
            )}
        </>
      )}
    </div>
  );
};

const Interviewers = () => {
  const opportunity = useCompanyScheduleInterviewSheet((s) => s.opportunity);
  const interview = useCompanyScheduleInterviewSheet((s) => s.interview);
  const updateInterview = useCompanyScheduleInterviewSheet(
    (s) => s.setInterview,
  );

  const { companyTeam, companyTeamLoading } = useCompanyTeamQuery(
    opportunity?.companyRecordId,
  );

  const interviewers = useMemo(() => {
    return (
      interview?.participants.map((id) => {
        const teamMember = companyTeam?.find(
          (teamMember) => teamMember.id === id,
        );

        return {
          id: teamMember?.id,
          name: teamMember?.name,
          email: teamMember?.email,
          profilePictureLink: teamMember?.profilePictureLink,
        };
      }) ?? []
    );
  }, [interview, companyTeam]);

  if (
    !interview ||
    !opportunity?.companyRecordId ||
    !opportunity.id ||
    companyTeamLoading
  ) {
    return;
  }

  return (
    <div className="border border-border shadow-md rounded-lg p-4 space-y-4 mt-6">
      <h5 className="text-sm font-semibold">Interviewer(s)</h5>
      {!interviewers.length ? (
        <p className="text-sm font-medium text-destructive">
          No interviewers specified
        </p>
      ) : (
        interviewers.map((interviewer) => {
          return (
            <div
              key={`interviewer-${interviewer.id}`}
              className="flex flex-row gap-3 items-center"
            >
              <Avatar className="size-8 min-w-8">
                {interviewer.profilePictureLink && (
                  <AvatarImage
                    src={interviewer.profilePictureLink}
                    className="object-cover object-center"
                  />
                )}
                <AvatarFallback>
                  <User className="size-6 text-accent-og-foreground" />
                </AvatarFallback>
              </Avatar>
              <span className="text-[#0C0D0D] text-sm font-medium">
                {interviewer.name}
              </span>
            </div>
          );
        })
      )}
      <CreateUpdateInterviewDialog
        interview={interview}
        jobOpeningId={opportunity.id}
        companyId={opportunity?.companyRecordId}
        onSuccess={updateInterview}
      />
    </div>
  );
};

const RequestAvailability = ({ closeParent }: { closeParent: () => void }) => {
  const [open, setOpen] = useState(false);

  const candidate = useCompanyScheduleInterviewSheet((s) => s.candidate);
  const [opportunityID, interviewID] = useCompanyScheduleInterviewSheet(
    useShallow((s) => [s.opportunity?.id, s.interview?.id]),
  );

  const { scheduleInterviewPending } = useCompanyScheduleInterview();
  const { requestAvailability, isRequesting } = useRequestInterviewAvailability(
    () => setOpenValue(false),
  );

  const { candidateAvailability } = useCompanyCandidateAvailabilityQuery(
    opportunityID,
    interviewID,
    candidate?.candidateId,
  );

  const hasNoAvailability = useMemo(
    () =>
      !candidateAvailability?.general.some((day) =>
        day.some(({ placeholder }) => !placeholder),
      ) && !candidateAvailability?.specific.length,
    [candidateAvailability],
  );

  const setOpenValue = (open: boolean) => {
    setOpen(open);

    if (!open) {
      closeParent();
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpenValue}>
      <DialogTrigger asChild>
        <Button className="mt-2 w-full" loading={scheduleInterviewPending}>
          {hasNoAvailability
            ? "Request Availability"
            : "Request Additional Availability"}
        </Button>
      </DialogTrigger>
      <DialogContent
        className="w-full p-0 sm:max-w-2xl"
        onClick={(event) => event.stopPropagation()}
        disableOverlayColor
      >
        <DialogHeader className="p-6">
          <div className="flex items-center gap-2">
            <DialogTitle>Request Candidate Availability</DialogTitle>
          </div>
          <DialogDescription className="sr-only">
            {hasNoAvailability
              ? 'Confirm that you need the candidate to provide availability for an interview and hit "Confirm"'
              : 'Confirm that you need the candidate to provide additional availability for an interview and hit "Confirm"'}
          </DialogDescription>
        </DialogHeader>
        <ScrollArea className="sm:max-h-[calc(100vh-10rem)] md:max-h-[70vh] px-6 pb-6">
          <p>
            By clicking &quot;Confirm,&quot; Venturous will reach out on your
            behalf to request the candidate update their availability for this
            interview.
          </p>
          <div className="flex justify-between">
            <Button
              type="button"
              className="mt-2"
              variant="outline"
              onClick={() => setOpen(false)}
            >
              Cancel
            </Button>
            <Button
              variant="secondary"
              type="submit"
              className="mt-2"
              disabled={isRequesting}
              onClick={() => {
                requestAvailability({
                  openingId: opportunityID,
                  candidateId: candidate?.candidateId,
                  interviewId: interviewID,
                });
              }}
            >
              {isRequesting ? <Loader2 className="animate-spin" /> : null}
              Confirm
            </Button>
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};
